// src/pages/DayVideos.js - SO USER FRIENDLY UPLOAD OVERLAY
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Button, Box, Card, CardContent, TextField,
    Grid, IconButton, CircularProgress, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, Chip, Modal, useTheme, Backdrop
} from '@mui/material';
import {
    Add, PlayArrow, Delete, Edit, DragIndicator, VideoFile,
    Visibility, Close, Save, Warning, Cancel, CloudUpload,
    KeyboardArrowLeft, Image as ImageIcon, CheckCircle
} from '@mui/icons-material';
import { videoAPI, dayAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VideoPlayer from '../components/VideoPlayer';
import AdminHeader from '../components/AdminHeader';

// ===== HELPER =====
const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('https://')) return url;
    if (url.startsWith('http://')) return url;
    return `${process.env.REACT_APP_API_URL}${url}`;
};

// ===== UPLOAD OVERLAY COMPONENT =====
const UploadOverlay = ({ progress, fileName, fileSize }) => {
    const isDone = progress >= 100;

    const getStatusText = () => {
        if (isDone) return '✅ Uploadot е завршен!';
        if (progress < 10) return '🔌 Поврзување со серверот...';
        if (progress < 30) return '📦 Подготвување на фајлот...';
        if (progress < 60) return '☁️ Uploadување на R2 Cloud...';
        if (progress < 90) return '⚡ Обработка на видеото...';
        return '🎬 Завршување...';
    };

    const getColor = () => isDone ? '#4CAF50' : '#ff7eb9';

    return (
        <Backdrop
            open={true}
            sx={{
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                backdropFilter: 'blur(8px)',
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 6,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, rgba(30,30,40,0.95), rgba(20,20,30,0.95))',
                border: `2px solid ${getColor()}40`,
                boxShadow: `0 0 60px ${getColor()}30`,
                maxWidth: 480,
                width: '90%',
                textAlign: 'center',
            }}>
                {/* Icon */}
                <Box sx={{
                    width: 100, height: 100,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${getColor()}30, ${getColor()}10)`,
                    border: `3px solid ${getColor()}50`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 3,
                    animation: isDone ? 'none' : 'pulse 2s infinite',
                    '@keyframes pulse': {
                        '0%': { boxShadow: `0 0 0 0 ${getColor()}40` },
                        '70%': { boxShadow: `0 0 0 20px ${getColor()}00` },
                        '100%': { boxShadow: `0 0 0 0 ${getColor()}00` },
                    }
                }}>
                    {isDone ? (
                        <CheckCircle sx={{ fontSize: 56, color: '#4CAF50' }} />
                    ) : (
                        <CloudUpload sx={{ fontSize: 56, color: '#ff7eb9' }} />
                    )}
                </Box>

                {/* Title */}
                <Typography variant="h5" sx={{
                    fontWeight: 'bold', mb: 1,
                    color: isDone ? '#4CAF50' : 'white'
                }}>
                    {isDone ? 'Успешно Uploadано!' : 'Се Uploadира...'}
                </Typography>

                {/* Status text */}
                <Typography variant="body1" sx={{ color: '#aaa', mb: 3 }}>
                    {getStatusText()}
                </Typography>

                {/* File info */}
                {fileName && (
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: '12px', p: 2, mb: 3, width: '100%'
                    }}>
                        <Typography variant="body2" sx={{ color: '#ff7eb9', fontWeight: 'bold' }}>
                            📁 {fileName}
                        </Typography>
                        {fileSize && (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                {fileSize}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Progress Bar */}
                <Box sx={{ width: '100%', mb: 2 }}>
                    <Box sx={{
                        width: '100%',
                        height: 12,
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                        <Box sx={{
                            width: `${progress}%`,
                            height: '100%',
                            background: isDone
                                ? 'linear-gradient(90deg, #4CAF50, #81C784)'
                                : 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                            borderRadius: 6,
                            transition: 'width 0.4s ease',
                            boxShadow: `0 0 10px ${getColor()}80`,
                        }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666' }}>0%</Typography>
                        <Typography variant="h6" sx={{ color: getColor(), fontWeight: 'bold' }}>
                            {progress}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>100%</Typography>
                    </Box>
                </Box>

                {/* Warning */}
                {!isDone && (
                    <Typography variant="caption" sx={{
                        color: '#555', mt: 1,
                        display: 'flex', alignItems: 'center', gap: 0.5
                    }}>
                        ⚠️ Не го затворајте прозорецот додека трае uploadо��
                    </Typography>
                )}

                {/* Done message */}
                {isDone && (
                    <Chip
                        label="☁️ Зачувано во R2 Cloud"
                        sx={{
                            bgcolor: 'rgba(76,175,80,0.15)',
                            color: '#4CAF50',
                            border: '1px solid rgba(76,175,80,0.3)',
                            fontWeight: 'bold', mt: 1
                        }}
                    />
                )}
            </Box>
        </Backdrop>
    );
};

// ===== VIDEO CARD =====
const VideoCardWide = ({ video, index, onDelete, onEdit, onPlay, onDragStart, onDragOver, onDrop, isDragging }) => {
    const theme = useTheme();
    const [thumbnailError, setThumbnailError] = useState(false);

    const thumbnailSrc = getMediaUrl(video.thumbnailUrl);
    const videoUrl = video.url && video.url.startsWith('https://')
        ? video.url
        : video.filePath && video.filePath.startsWith('https://')
            ? video.filePath
            : `${process.env.REACT_APP_API_URL}/videos/file/${video.id}`;

    return (
        <Card
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, index)}
            sx={{
                mb: 2, borderRadius: '20px',
                backgroundColor: theme.palette.background.paper,
                border: '1px solid rgba(255,126,185,0.2)',
                boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                overflow: 'hidden',
                cursor: 'grab', opacity: isDragging ? 0.5 : 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(255,126,185,0.25)',
                    borderColor: 'rgba(255,126,185,0.5)',
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={3}>
                    {/* Drag Handle */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#ff7eb9', minWidth: 40 }}>
                        <DragIndicator sx={{ fontSize: 24, mb: 1 }} />
                        <Chip label={video.orderIndex || index + 1} size="small"
                              sx={{ bgcolor: 'rgba(255,126,185,0.15)', color: '#ff7eb9', fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)' }} />
                    </Box>

                    {/* Thumbnail */}
                    <Box sx={{ position: 'relative', minWidth: 160, height: 90, cursor: 'pointer' }} onClick={() => onPlay(video)}>
                        {thumbnailSrc && !thumbnailError ? (
                            <img src={thumbnailSrc} alt={video.title}
                                 style={{ width: '160px', height: '90px', objectFit: 'cover', borderRadius: '10px' }}
                                 onError={() => setThumbnailError(true)} />
                        ) : (
                            <video src={videoUrl}
                                   style={{ width: '160px', height: '90px', objectFit: 'cover', borderRadius: '10px',
                                       backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5' }}
                                   muted preload="metadata" />
                        )}
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'rgba(255,126,185,0.9)', borderRadius: '50%',
                            width: 44, height: 44, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(255,126,185,0.5)'
                        }}>
                            <PlayArrow sx={{ color: 'white', fontSize: 26 }} />
                        </Box>
                        {thumbnailSrc && !thumbnailError && (
                            <Chip label="🖼️" size="small" sx={{
                                position: 'absolute', top: 4, right: 4,
                                bgcolor: '#AB47BC', color: 'white', fontSize: '10px', height: '20px'
                            }} />
                        )}
                    </Box>

                    {/* Video Details */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                            {video.title}
                        </Typography>
                        {video.description && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
                                {video.description}
                            </Typography>
                        )}
                        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                            {video.fileName && (
                                <Chip label={`📁 ${video.fileName}`} variant="outlined" size="small"
                                      sx={{ borderColor: 'rgba(255,126,185,0.3)', color: theme.palette.text.secondary }} />
                            )}
                            <Chip label={`ID: ${video.id}`} variant="outlined" size="small"
                                  sx={{ borderColor: 'rgba(255,126,185,0.3)', color: theme.palette.text.secondary }} />
                            {video.fileSize && (
                                <Chip label={`📊 ${(video.fileSize / (1024 * 1024)).toFixed(1)} MB`} variant="outlined" size="small"
                                      sx={{ borderColor: 'rgba(255,126,185,0.3)', color: theme.palette.text.secondary }} />
                            )}
                            {video.url && video.url.startsWith('https://') && (
                                <Chip label="☁️ R2 Cloud" size="small"
                                      sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#4CAF50', border: '1px solid rgba(76,175,80,0.3)' }} />
                            )}
                        </Box>
                    </Box>

                    {/* Actions */}
                    <Box display="flex" flexDirection="column" gap={1} minWidth={130}>
                        <Button variant="contained" startIcon={<Visibility />} onClick={() => onPlay(video)} fullWidth
                                sx={{
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    borderRadius: '10px', textTransform: 'none', fontWeight: 'bold',
                                    '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                                }}>
                            Прегледај
                        </Button>
                        <Button variant="outlined" startIcon={<Edit />} onClick={onEdit} fullWidth
                                sx={{
                                    borderColor: '#a5d8ff', color: '#a5d8ff',
                                    borderRadius: '10px', textTransform: 'none',
                                    '&:hover': { bgcolor: 'rgba(165,216,255,0.1)', borderColor: '#74c0fc' }
                                }}>
                            Уреди
                        </Button>
                        <Button variant="outlined" startIcon={<Delete />} onClick={() => onDelete(video)} fullWidth
                                sx={{
                                    borderColor: '#ff6b6b', color: '#ff6b6b',
                                    borderRadius: '10px', textTransform: 'none',
                                    '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' }
                                }}>
                            Избриши
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ===== ADD VIDEO DIALOG =====
const AddVideoDialog = ({ open, onClose, onSubmit, dayId, nextOrderIndex }) => {
    const theme = useTheme();
    const [formData, setFormData] = useState({ title: '', description: '', orderIndex: nextOrderIndex || 1 });
    const [file, setFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) setFormData(prev => ({ ...prev, orderIndex: nextOrderIndex || 1 }));
    }, [open, nextOrderIndex]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Насловот е задолжителен';
        if (!file) newErrors.file = 'Видео фајлот е задолжителен';
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        onSubmit(formData, file, thumbnailFile);
        handleClose();
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (!selectedFile.type.startsWith('video/')) { setErrors({ ...errors, file: 'Одберете видео фајл' }); return; }
            setFile(selectedFile);
            setPreview(URL.createObjectURL(selectedFile));
            setErrors({ ...errors, file: '' });
            if (!formData.title) {
                setFormData(prev => ({ ...prev, title: selectedFile.name.replace(/\.[^/.]+$/, "") }));
            }
        }
    };

    const handleThumbnailChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type.startsWith('image/')) {
            setThumbnailFile(selectedFile);
            setThumbnailPreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleClose = () => {
        setFormData({ title: '', description: '', orderIndex: nextOrderIndex || 1 });
        setFile(null); setThumbnailFile(null);
        setPreview(null); setThumbnailPreview(null);
        setErrors({}); onClose();
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover fieldset': { borderColor: '#ff7eb9' },
            '&.Mui-focused fieldset': { borderColor: '#ff7eb9' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#ff7eb9' }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '20px', backgroundColor: theme.palette.background.paper, border: '2px solid rgba(255,126,185,0.3)' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', color: 'white', py: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Add />
                    <Typography variant="h5" fontWeight="bold">🎬 Додади Ново Видео</Typography>
                </Box>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Наслов на видео *"
                                       value={formData.title}
                                       onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                       error={!!errors.title} helperText={errors.title} sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Опис" multiline rows={3}
                                       value={formData.description}
                                       onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                       sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label="Редослед"
                                       value={formData.orderIndex}
                                       onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 1 })}
                                       inputProps={{ min: 1 }} sx={fieldSx} />
                        </Grid>

                        {/* Video File */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                border: '2px dashed rgba(255,126,185,0.4)', borderRadius: '16px',
                                p: 3, textAlign: 'center',
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,126,185,0.05)' : 'rgba(255,126,185,0.03)',
                                minHeight: 200
                            }}>
                                <Typography variant="h6" sx={{ color: '#ff7eb9', mb: 2 }}>📹 Видео фајл *</Typography>
                                <Button variant="outlined" component="label" startIcon={<CloudUpload />}
                                        sx={{ borderRadius: '10px', color: '#ff7eb9', borderColor: '#ff7eb9', mb: 2,
                                            '&:hover': { bgcolor: 'rgba(255,126,185,0.1)' } }}>
                                    Избери видео
                                    <input type="file" hidden accept="video/*" onChange={handleFileChange} />
                                </Button>
                                {preview && <video src={preview} controls style={{ maxWidth: '100%', height: '100px', borderRadius: '8px' }} />}
                                {file && <Typography variant="body2" sx={{ color: '#4CAF50', mt: 1 }}>✅ {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)</Typography>}
                                {errors.file && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{errors.file}</Alert>}
                            </Box>
                        </Grid>

                        {/* Thumbnail */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                border: '2px dashed rgba(171,71,188,0.4)', borderRadius: '16px',
                                p: 3, textAlign: 'center',
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(171,71,188,0.05)' : 'rgba(171,71,188,0.03)',
                                minHeight: 200
                            }}>
                                <Typography variant="h6" sx={{ color: '#AB47BC', mb: 2 }}>🖼️ Thumbnail (опционално)</Typography>
                                {thumbnailPreview ? (
                                    <Box>
                                        <img src={thumbnailPreview} alt="Thumbnail"
                                             style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '8px', border: '2px solid #AB47BC' }} />
                                        <Box sx={{ mt: 1 }}>
                                            <Button size="small" color="error"
                                                    onClick={() => { setThumbnailFile(null); setThumbnailPreview(null); }}>
                                                ❌ Отстрани
                                            </Button>
                                            <Button size="small" component="label" sx={{ color: '#AB47BC', ml: 1 }}>
                                                🔄 Промени
                                                <input type="file" hidden accept="image/*" onChange={handleThumbnailChange} />
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Button variant="outlined" component="label" startIcon={<ImageIcon />}
                                            sx={{ borderRadius: '10px', color: '#AB47BC', borderColor: '#AB47BC',
                                                '&:hover': { bgcolor: 'rgba(171,71,188,0.1)' } }}>
                                        Избери слика
                                        <input type="file" hidden accept="image/*" onChange={handleThumbnailChange} />
                                    </Button>
                                )}
                                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', mt: 2 }}>
                                    JPG, PNG (макс. 10MB) • Препорачано: 1280x720 px
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={handleClose} sx={{ borderRadius: '10px', color: theme.palette.text.secondary }}>Откажи</Button>
                    <Button type="submit" variant="contained"
                            sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', borderRadius: '10px', fontWeight: 'bold', px: 3,
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' } }}>
                        Додади Видео
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

// ===== EDIT VIDEO DIALOG =====
const EditVideoDialog = ({ open, onClose, onSubmit, video, dayId }) => {
    const theme = useTheme();
    const [formData, setFormData] = useState({ title: '', description: '', orderIndex: 1 });
    const [newFile, setNewFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (video && open) {
            setFormData({ title: video.title || '', description: video.description || '', orderIndex: video.orderIndex || 1 });
            setNewFile(null); setThumbnailFile(null); setThumbnailPreview(null);
            setShowFileUpload(false); setPreview(null); setErrors({});
        }
    }, [video, open]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.title.trim()) { setErrors({ title: 'Насловот е задолжителен' }); return; }
        onSubmit(formData, newFile || null, !!newFile, thumbnailFile);
    };

    const handleClose = () => {
        setFormData({ title: '', description: '', orderIndex: 1 });
        setNewFile(null); setThumbnailFile(null); setThumbnailPreview(null);
        setShowFileUpload(false); setPreview(null); setErrors({}); onClose();
    };

    const currentThumbnailSrc = getMediaUrl(video?.thumbnailUrl);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover fieldset': { borderColor: '#ff7eb9' },
            '&.Mui-focused fieldset': { borderColor: '#ff7eb9' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#ff7eb9' }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '20px', backgroundColor: theme.palette.background.paper, border: '2px solid rgba(255,126,185,0.3)' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(45deg, #ffa726, #FF7043)', color: 'white', py: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Edit />
                    <Typography variant="h5" fontWeight="bold">✏️ Уреди Видео</Typography>
                </Box>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Наслов" value={formData.title}
                                       onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                       error={!!errors.title} helperText={errors.title} sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Опис" multiline rows={3}
                                       value={formData.description}
                                       onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                       sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label="Редослед"
                                       value={formData.orderIndex}
                                       onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 1 })}
                                       inputProps={{ min: 1 }} sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{
                                border: '2px dashed rgba(171,71,188,0.4)', borderRadius: '16px',
                                p: 3, textAlign: 'center',
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(171,71,188,0.05)' : 'rgba(171,71,188,0.03)'
                            }}>
                                <Typography variant="h6" sx={{ color: '#AB47BC', mb: 2 }}>🖼️ Thumbnail</Typography>
                                {thumbnailPreview ? (
                                    <Box>
                                        <img src={thumbnailPreview} alt="New Thumbnail"
                                             style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: '8px', border: '2px solid #AB47BC' }} />
                                        <Typography variant="body2" sx={{ color: '#4CAF50', mt: 1 }}>✅ Нов thumbnail одбран</Typography>
                                        <Button size="small" color="error"
                                                onClick={() => { setThumbnailFile(null); setThumbnailPreview(null); }} sx={{ mt: 1 }}>
                                            ❌ Отстрани
                                        </Button>
                                    </Box>
                                ) : currentThumbnailSrc ? (
                                    <Box>
                                        <img src={currentThumbnailSrc} alt="Current Thumbnail"
                                             style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: '8px', border: '2px solid #AB47BC' }} />
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                                            Тековен thumbnail {video?.thumbnailUrl?.startsWith('https://') ? '☁️ R2' : '📁 Локален'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>Нема thumbnail</Typography>
                                )}
                                <Button variant="outlined" component="label" startIcon={<ImageIcon />}
                                        sx={{ borderRadius: '10px', color: '#AB47BC', borderColor: '#AB47BC', mt: 2,
                                            '&:hover': { bgcolor: 'rgba(171,71,188,0.1)' } }}>
                                    {currentThumbnailSrc ? 'Промени thumbnail' : 'Додади thumbnail'}
                                    <input type="file" hidden accept="image/*"
                                           onChange={(e) => {
                                               const f = e.target.files[0];
                                               if (f && f.type.startsWith('image/')) {
                                                   setThumbnailFile(f);
                                                   setThumbnailPreview(URL.createObjectURL(f));
                                               }
                                           }} />
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    variant={showFileUpload ? "outlined" : "contained"}
                                    onClick={() => setShowFileUpload(!showFileUpload)}
                                    startIcon={showFileUpload ? <Cancel /> : <CloudUpload />}
                                    sx={showFileUpload ? {
                                        borderRadius: '10px', color: '#ff6b6b', borderColor: '#ff6b6b',
                                        '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' }
                                    } : {
                                        borderRadius: '10px',
                                        background: 'linear-gradient(45deg, #ffa726, #FF7043)',
                                        color: 'white', fontWeight: 'bold',
                                        '&:hover': { background: 'linear-gradient(45deg, #fb8c00, #F4511E)' }
                                    }}>
                                    {showFileUpload ? 'Откажи менување' : '🔄 Смени видео фајл'}
                                </Button>
                            </Box>
                        </Grid>
                        {showFileUpload && (
                            <Grid item xs={12}>
                                <Box sx={{
                                    border: '2px dashed rgba(255,167,38,0.4)', borderRadius: '16px',
                                    p: 3, textAlign: 'center',
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,167,38,0.05)' : 'rgba(255,167,38,0.03)'
                                }}>
                                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
                                        Старото видео ќе биде избришано!
                                    </Alert>
                                    <input type="file" accept="video/*"
                                           onChange={(e) => {
                                               const f = e.target.files[0];
                                               if (f && f.type.startsWith('video/')) {
                                                   setNewFile(f);
                                                   setPreview(URL.createObjectURL(f));
                                               }
                                           }}
                                           style={{
                                               width: '100%', padding: '12px',
                                               border: '2px solid rgba(255,167,38,0.4)',
                                               borderRadius: '10px',
                                               color: theme.palette.text.primary
                                           }} />
                                    {preview && <video src={preview} controls style={{ maxWidth: '100%', height: '150px', borderRadius: '10px', marginTop: '12px' }} />}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={handleClose} sx={{ borderRadius: '10px', color: theme.palette.text.secondary }}>Откажи</Button>
                    <Button type="submit" variant="contained" startIcon={<Save />}
                            sx={{ background: 'linear-gradient(45deg, #ffa726, #FF7043)', borderRadius: '10px', fontWeight: 'bold', px: 3,
                                '&:hover': { background: 'linear-gradient(45deg, #fb8c00, #F4511E)' } }}>
                        {newFile ? 'Смени Видео' : 'Зачувај'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

// ===== MAIN COMPONENT =====
const DayVideos = () => {
    const { dayId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { user, isAuthenticated, loading: authLoading } = useAuth();

    const [videos, setVideos] = useState([]);
    const [day, setDay] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openVideoPlayer, setOpenVideoPlayer] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) navigate('/login');
    }, [authLoading, isAuthenticated, user, navigate]);

    const fetchData = async () => {
        if (!isAuthenticated || user?.role !== 'ADMIN') return;
        try {
            setLoading(true); setError('');
            try {
                const dayData = await dayAPI.admin.getById(dayId);
                setDay(dayData);
            } catch (dayError) {
                if (dayError.response?.status === 401) { navigate('/login'); return; }
                setError('Денот не е пронајден'); setLoading(false); return;
            }
            try {
                const videosData = await videoAPI.admin.getByDay(dayId);
                setVideos(Array.isArray(videosData)
                    ? videosData.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                    : []);
            } catch (videoError) {
                if (videoError.response?.status === 401) { navigate('/login'); return; }
                setVideos([]);
            }
        } catch (err) {
            if (err.response?.status === 401) { navigate('/login'); return; }
            setError('Грешка при вчитување на податоците');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated && user?.role === 'ADMIN' && dayId) fetchData();
    }, [authLoading, isAuthenticated, user, dayId]);

    const handleAddVideo = async (videoData, file, thumbnailFile = null) => {
        try {
            setUploadProgress(0);
            setIsUploading(true);
            setUploadingFile({
                name: file.name,
                size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`
            });
            setError('');

            const savedVideo = await videoAPI.admin.upload(
                file, videoData.title, videoData.description,
                videoData.orderIndex, parseInt(dayId),
                (progress) => setUploadProgress(progress)
            );

            if (thumbnailFile && savedVideo?.id) {
                await videoAPI.admin.uploadThumbnail(savedVideo.id, thumbnailFile);
            }

            // Pokazhi 100% za 1 sekunda pred zatvoranje
            setUploadProgress(100);
            setTimeout(() => {
                setIsUploading(false);
                setUploadProgress(0);
                setUploadingFile(null);
                setSuccess('✅ Видеото е успешно додадено во R2 Cloud!');
                setTimeout(() => setSuccess(''), 4000);
                fetchData();
            }, 1500);

        } catch (err) {
            setIsUploading(false);
            setUploadProgress(0);
            setUploadingFile(null);
            if (err.response?.status === 401) { navigate('/login'); return; }
            setError('Грешка при додавање на видео: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleEditVideo = async (videoData, newFile = null, replaceFile = false, thumbnailFile = null) => {
        try {
            setError('');
            if (replaceFile && newFile) {
                setUploadProgress(0);
                setIsUploading(true);
                setUploadingFile({
                    name: newFile.name,
                    size: `${(newFile.size / (1024 * 1024)).toFixed(2)} MB`
                });
                await videoAPI.admin.delete(selectedVideo.id);
                const savedVideo = await videoAPI.admin.upload(
                    newFile, videoData.title, videoData.description,
                    videoData.orderIndex, parseInt(dayId),
                    (progress) => setUploadProgress(progress)
                );
                if (thumbnailFile && savedVideo?.id) {
                    await videoAPI.admin.uploadThumbnail(savedVideo.id, thumbnailFile);
                }
                setUploadProgress(100);
                setTimeout(() => {
                    setIsUploading(false);
                    setUploadProgress(0);
                    setUploadingFile(null);
                    setSuccess('✅ Видеото е успешно ажурирано!');
                    setTimeout(() => setSuccess(''), 4000);
                    fetchData();
                }, 1500);
            } else {
                await videoAPI.admin.update(selectedVideo.id, {
                    title: videoData.title, description: videoData.description, orderIndex: videoData.orderIndex
                });
                if (thumbnailFile) await videoAPI.admin.uploadThumbnail(selectedVideo.id, thumbnailFile);
                setSuccess('✅ Видеото е успешно ажурирано!');
                setTimeout(() => setSuccess(''), 4000);
                fetchData();
            }
            setOpenEditDialog(false);
            setSelectedVideo(null);
        } catch (err) {
            setIsUploading(false);
            setUploadProgress(0);
            setUploadingFile(null);
            if (err.response?.status === 401) { navigate('/login'); return; }
            setError('Грешка при ажурирање на видео: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedVideo) return;
        try {
            setDeleteLoading(true);
            await videoAPI.admin.delete(selectedVideo.id);
            setOpenDeleteDialog(false); setSelectedVideo(null);
            setSuccess('✅ Видеото е успешно избришано!');
            setTimeout(() => setSuccess(''), 3000);
            fetchData();
        } catch (err) {
            if (err.response?.status === 401) { navigate('/login'); return; }
            setError('Грешка при бришење на видео: ' + (err.response?.data?.message || err.message));
        } finally { setDeleteLoading(false); }
    };

    const handleBackToDays = () => {
        if (day?.programs?.length > 0) navigate(`/admin/programs/${day.programs[0].id}/days`);
        else navigate('/admin/days');
    };

    const handleDragStart = (e, index) => { setDraggedIndex(index); e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const handleDrop = async (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) { setDraggedIndex(null); return; }
        const newVideos = [...videos];
        const [dragged] = newVideos.splice(draggedIndex, 1);
        newVideos.splice(targetIndex, 0, dragged);
        const updated = newVideos.map((v, i) => ({ ...v, orderIndex: i + 1 }));
        setVideos(updated); setDraggedIndex(null);
        try { await videoAPI.admin.reorder(updated.map(v => v.id)); }
        catch (err) { setError('Грешка при ажурирање на редоследот'); fetchData(); }
    };

    if (authLoading || loading) {
        return (
            <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default,
                display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
                    {authLoading ? 'Проверка на овластувања...' : 'Вчитување на видеа...'}
                </Typography>
            </Box>
        );
    }

    if (!isAuthenticated || user?.role !== 'ADMIN') return null;

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>

            {/* ✅ UPLOAD OVERLAY */}
            {isUploading && (
                <UploadOverlay
                    progress={uploadProgress}
                    fileName={uploadingFile?.name}
                    fileSize={uploadingFile?.size}
                />
            )}

            <Container maxWidth="lg">
                <AdminHeader />

                {/* Back Button */}
                <Box sx={{ mb: 4 }}>
                    <Button onClick={handleBackToDays}
                            startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                            variant="outlined"
                            sx={{
                                color: '#a5d8ff', borderColor: '#a5d8ff',
                                backgroundColor: 'rgba(165,216,255,0.06)',
                                fontWeight: 'bold', fontSize: '1rem',
                                px: 3, py: 1.5, borderRadius: '12px', textTransform: 'none',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: '#a5d8ff', color: '#2b2b2b',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(165,216,255,0.3)'
                                }
                            }}>
                        Назад кон денови
                    </Button>
                </Box>

                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: 2.5, borderRadius: '20px',
                        backgroundColor: theme.palette.background.paper,
                        border: '1px solid rgba(255,126,185,0.2)',
                        boxShadow: '0 4px 24px rgba(255,126,185,0.08)'
                    }}>
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '14px',
                            background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '28px', boxShadow: '0 4px 12px rgba(255,126,185,0.3)'
                        }}>🎬</Box>
                        <Box>
                            <Typography variant="h3" sx={{
                                fontWeight: 900,
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                {day?.title || `Ден #${dayId}`}
                            </Typography>
                            <Chip label={`${videos.length} видеа`} size="small" sx={{
                                bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                                fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)', mt: 0.5
                            }} />
                        </Box>
                    </Box>
                    <Button variant="contained" size="large" startIcon={<Add />}
                            onClick={() => setOpenAddDialog(true)}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                px: 4, py: 1.5, borderRadius: '12px',
                                fontSize: '1rem', fontWeight: 'bold', textTransform: 'none',
                                boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                                },
                                transition: 'all 0.3s ease'
                            }}>
                        Додади Видео
                    </Button>
                </Box>

                {/* Messages */}
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccess('')}>{success}</Alert>}

                {/* Videos List */}
                {videos.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 10,
                        border: '2px dashed rgba(255,126,185,0.3)',
                        borderRadius: '20px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,126,185,0.05)' : 'rgba(255,126,185,0.03)'
                    }}>
                        <VideoFile sx={{ fontSize: 80, color: '#ff7eb9', mb: 3 }} />
                        <Typography variant="h4" sx={{ color: '#ff7eb9', fontWeight: 'bold', mb: 2 }}>
                            Нема видеа
                        </Typography>
                        <Button variant="contained" size="large" startIcon={<Add />}
                                onClick={() => setOpenAddDialog(true)}
                                sx={{
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    borderRadius: '12px', px: 4, py: 1.5,
                                    fontWeight: 'bold', textTransform: 'none',
                                    '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                                }}>
                            Додади Прво Видео
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h5" sx={{ color: '#ff7eb9', fontWeight: 'bold', mb: 3 }}>
                            📋 Листа на видеа ({videos.length})
                        </Typography>
                        {videos.map((video, index) => (
                            <VideoCardWide key={video.id} video={video} index={index}
                                           onDelete={() => { setSelectedVideo(video); setOpenDeleteDialog(true); }}
                                           onEdit={() => { setSelectedVideo(video); setOpenEditDialog(true); }}
                                           onPlay={() => { setSelectedVideo(video); setOpenVideoPlayer(true); }}
                                           onDragStart={handleDragStart}
                                           onDragOver={handleDragOver}
                                           onDrop={handleDrop}
                                           isDragging={draggedIndex === index}
                            />
                        ))}
                    </Box>
                )}
            </Container>

            {/* Video Player Modal */}
            <Modal open={openVideoPlayer} onClose={() => setOpenVideoPlayer(false)}
                   sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: '16px', boxShadow: 24,
                    position: 'relative', maxWidth: '90vw', maxHeight: '90vh',
                    border: '2px solid rgba(255,126,185,0.3)'
                }}>
                    <IconButton onClick={() => setOpenVideoPlayer(false)}
                                sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', zIndex: 1000 }}>
                        <Close />
                    </IconButton>
                    {selectedVideo && <VideoPlayer video={selectedVideo} />}
                </Box>
            </Modal>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setSelectedVideo(null); }}
                    maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', backgroundColor: theme.palette.background.paper, border: '2px solid rgba(255,126,185,0.3)' } }}>
                <DialogTitle sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', color: 'white', fontWeight: 'bold' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning />
                        <Typography variant="h6">⚠️ Потврди Бришење</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.primary }}>
                        Дали сте сигурни дека сакате да го избришете видеото
                    </Typography>
                    <Typography sx={{ color: '#ff7eb9', fontWeight: 'bold', fontSize: '1.1rem', mt: 1 }}>
                        "{selectedVideo?.title}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button onClick={() => { setOpenDeleteDialog(false); setSelectedVideo(null); }} disabled={deleteLoading}
                            sx={{ borderRadius: '10px', color: theme.palette.text.secondary }}>
                        Откажи
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" disabled={deleteLoading}
                            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Delete />}
                            sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', borderRadius: '10px', fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' } }}>
                        {deleteLoading ? 'Се брише...' : 'Избриши'}
                    </Button>
                </DialogActions>
            </Dialog>

            <AddVideoDialog open={openAddDialog}
                            onClose={() => setOpenAddDialog(false)}
                            onSubmit={handleAddVideo} dayId={dayId} nextOrderIndex={videos.length + 1} />

            <EditVideoDialog open={openEditDialog}
                             onClose={() => { setOpenEditDialog(false); setSelectedVideo(null); }}
                             onSubmit={handleEditVideo} video={selectedVideo} dayId={dayId} />
        </Box>
    );
};

export default DayVideos;